import { Enterprise } from '@mews-commander/data-access';
import { IconName } from '@optimus-web/core';
import { LocalizedText, useFeatureFlag } from '@mews/framework';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import {
    hasCurrentEnterpriseServiceId,
    getCurrentEnterpriseUserPreferredServiceId,
    getCurrentEnterprise,
} from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import {
    hasCurrentEnterpriseServiceProviderMode,
    isCurrentEnterpriseActivityReportEnabled,
} from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import { canCurrentProfileRunInstallations } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { canPlatformExtendEHW } from '@mews-commander/legacy-entities/src/ducks/scopes';
import { useCommanderSelector } from '@mews-commander/utils';
import { RESOURCE_STATUS_REPORT_SHORTCUT } from '../../shortcuts';
import { createListItemWithLink } from '../helpers';
import { SidebarExpandableListItem, SidebarFontIcon } from '../style';

interface OperationMenuProps {
    value: string;
    opened?: boolean;
    onClicked?: () => void;
    depth?: number;
}

export const OperationMenu: React.FC<OperationMenuProps> = (props) => {
    const isInstallationRunner = useCommanderSelector(canCurrentProfileRunInstallations);
    const hasServiceProviderMode = useCommanderSelector(hasCurrentEnterpriseServiceProviderMode);
    const hasReservableService = useCommanderSelector(hasCurrentEnterpriseServiceId);
    const currentEnterprise: Enterprise = useCommanderSelector(getCurrentEnterprise);
    const serviceId = useCommanderSelector(getCurrentEnterpriseUserPreferredServiceId);
    const activityReportEnabled = useCommanderSelector(isCurrentEnterpriseActivityReportEnabled);
    const canExtendEHW = useCommanderSelector(canPlatformExtendEHW);
    const newGuestInHouseReportRouteEnabled = useFeatureFlag(FeatureFlag.newGuestInHouseReportRouteEnabled);

    return (
        <SidebarExpandableListItem
            leftIcon={<SidebarFontIcon value={IconName.Operations} />}
            primaryText={<LocalizedText textKey={TextKey.OperationPlural} />}
            {...props}
        >
            {isInstallationRunner && (
                <>
                    {createListItemWithLink({
                        textKey: TextKey.EnterpriseBuilder,
                        iconName: IconName.Configuration,
                        path: 'Installation/EnterpriseBuilder/Index',
                    })}
                    {createListItemWithLink({
                        textKey: TextKey.EnterpriseBuilderPro,
                        iconName: IconName.Configuration,
                        path: 'Installation/EnterpriseBuilderPro/Index',
                    })}
                    {createListItemWithLink({
                        textKey: TextKey.Import,
                        iconName: IconName.Installations,
                        path: 'Import/Index',
                    })}
                </>
            )}
            {hasServiceProviderMode && currentEnterprise?.id && (
                <>
                    {hasReservableService &&
                        createListItemWithLink({
                            textKey: TextKey.GuestInHouseReport,
                            iconName: IconName.Home,
                            path: newGuestInHouseReportRouteEnabled
                                ? 'GuestsInHouseReport/Index'
                                : 'GuestInHouseReport/Index',
                            search: { 'Service.Id': serviceId },
                        })}

                    {createListItemWithLink({
                        textKey: TextKey.ActionLogItemReport,
                        iconName: IconName.ActionLog,
                        path: 'EventReport/Index',
                    })}

                    {createListItemWithLink({
                        textKey: TextKey.ResourceStatusReport,
                        iconName: IconName.SpaceStatus,
                        path: 'ResourceStatusReport/Index',
                        tooltip: RESOURCE_STATUS_REPORT_SHORTCUT,
                    })}

                    {activityReportEnabled &&
                        createListItemWithLink({
                            textKey: TextKey.ActivityReport,
                            iconName: IconName.ActivityReport,
                            path: 'ActivityReport/Index',
                        })}

                    {hasReservableService && (
                        <>
                            {createListItemWithLink({
                                textKey: TextKey.ProductReport,
                                iconName: IconName.ProductReport,
                                path: 'ProductReport/Index',
                                search: {
                                    Mode: 'New',
                                    'Service.Id': serviceId,
                                },
                            })}

                            {createListItemWithLink({
                                textKey: TextKey.ProductCheckList,
                                iconName: IconName.ProductReport,
                                path: 'ProductCheckList/Index',
                            })}
                        </>
                    )}
                </>
            )}
            {canExtendEHW &&
                !currentEnterprise.id &&
                createListItemWithLink({
                    textKey: TextKey.ExtendEHW,
                    iconName: IconName.Installations,
                    path: 'Enterprises/ExtendEHW',
                })}
        </SidebarExpandableListItem>
    );
};
